/**
 * openScope coordinate parsing. The airport JSON accepts several formats for
 * latitude/longitude/elevation, e.g.
 *   ["N37d37.13", "W122d22.53", "13ft"]
 *   ["N40d56m48.65", "W076d37m02.20", "866ft"]
 *   [40.94684722, -76.61727778, "866ft"]
 * Elevation strings are "<n>ft" or "<n>m"; a bare number is feet.
 */
export type OpenScopePosition = [string | number, string | number, (string | number)?];

const DMS = /^([NSEW])(\d+(?:\.\d+)?)(?:d(\d+(?:\.\d+)?))?(?:m(\d+(?:\.\d+)?))?$/i;

export function parseOpenScopeAngle(v: string | number): number {
  if (typeof v === 'number') return v;
  const s = v.trim();
  const m = DMS.exec(s);
  if (!m) {
    const n = Number(s);
    if (Number.isNaN(n)) throw new Error(`Unparseable coordinate: ${v}`);
    return n;
  }
  const hemi = m[1]!.toUpperCase();
  const deg = Number(m[2]);
  const min = m[3] !== undefined ? Number(m[3]) : 0;
  const sec = m[4] !== undefined ? Number(m[4]) : 0;
  const value = deg + min / 60 + sec / 3600;
  return hemi === 'S' || hemi === 'W' ? -value : value;
}

export function parseOpenScopeElevationMeters(v: string | number | undefined): number {
  if (v === undefined) return 0;
  if (typeof v === 'number') return v * 0.3048;
  const s = v.trim().toLowerCase();
  if (s.endsWith('ft')) return Number(s.slice(0, -2)) * 0.3048;
  if (s.endsWith('m')) return Number(s.slice(0, -1));
  return Number(s) * 0.3048;
}

/** Returns [latDeg, lonDeg, elevationMeters]. */
export function parseOpenScopeCoordinate(p: OpenScopePosition): [number, number, number] {
  return [parseOpenScopeAngle(p[0]), parseOpenScopeAngle(p[1]), parseOpenScopeElevationMeters(p[2])];
}
